import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSliderModule } from '@angular/material/slider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SharedModule } from '@fi-shared/shared.module';
import { UtilitiesCommonModule } from './common/common.module';
import { AddressComponent } from './address/address.component';
import { AddressesComponent } from './addresses/addresses.component';
import { PageSectionErrorComponent } from './section-select/page-section-error/page-section-error.component';
import { SectionSelectComponent } from './section-select/section-select.component';
import { MobileTabsComponent } from './mobile-tabs/mobile-tabs.component';
import { DeviceSwitchComponent } from './device-switch/device-switch.component';
import { DeviceSwitchHandlerDirective } from './device-switch/device-switch-handler.directive';
import { TabsComponent } from './tabs/tabs.component';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { ObjectTypesService } from './fi-services/object-types.service';
import { ErrorMessageService } from './fi-services/error-message.service';
import { ObjectTitleService } from './fi-services/object-title.service';
import { ItemActivationComponent } from './item-activation/item-activation.component';
import { EmailAddressContactsComponent } from './email-address-contacts/email-address-contacts.component';
import { MobileLanguagesSelectComponent } from './mobile-languages-select/mobile-languages-select.component';
import { MoreMenuMobileComponent } from './more-menu-mobile/more-menu-mobile.component';
import { TitleEditPipe } from './fi-pipes/title-edit.pipe';
import { TitleCreatePipe } from './fi-pipes/title-create.pipe';
import { YesNoPipe } from './fi-pipes/yes-no.pipe';
import { ContactService } from '@fi-shared/fi-contacts/contact.service';
import { UserProfileLinkComponent } from './user-profile-link/user-profile-link.component';
import { MoreMenuMobileDialogHandlerComponent } from './more-menu-mobile/more-menu-mobile-dialog-handler.component';
import { AuthenticateService } from './fi-services/authenticate.service';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { ContentLanguagesService } from './fi-services/content-languages.service';
import { CompareHtmlModule } from './compare-html/compare-html.module';
import { FiDisableLinksModule } from '../shared/fi-directives/disable-links/disable-links.module';
import { ListViewSettingsService } from './fi-services/list-view-settings.service';
import { ExtendedSearchService } from './fi-services/extended-search.service';
import { LocalSearchStorageService } from './fi-services/local-search-storage.service';
import { AccessRightService } from '@fi-shared/fi-shared-services/access-right.service';
import { SecondsToTimePipe } from './fi-pipes/seconds-to-time.pipe';
import { FiToggleVisibilityModule } from './toggle-visibility/toggle-visibility.module';
import { FiTelHrefModule } from './tel-href/tel-href.module';
import { FiReplaceWithLinksModule } from '../shared/fi-directives/replace-with-links/replace-with-links.module';
import { FiAutofocusModule } from '@fi-shared/fi-directives/autofocus/autofocus.module';
import { FiClickTriggerForModule } from './click-trigger-for/click-trigger-for.module';
import { FiDialogModule } from './dialog/dialog.module';
import { FiVersionsListModule } from './versions-list/versions-list.module';
import { FiRollupContentModule } from '@fi-shared/rollup/rollup-content.module';
import { FormValidatorService } from './fi-services/form-validator.service';
import { FiTagModule } from '@fi-shared/tag';
import { InsertMediaModule } from './insert-media/insert-media.module';
import { FileUploadModule } from './file-upload/file-upload.module';
import { SecondsToTimeService } from './fi-services/seconds-to-time.service';
import { InactivityCountdownHttpHandlerService } from './fi-services/inactivity-countdown-http-handler.service';
import { FiMobileAppHelpersModule } from '../mobile-app-helpers/mobile-app-helpers.module';
import { FiErrorMessageModule } from '@fi-shared/fi-error-message';
import { FiSideListNewModule } from './side-list-new/side-list-new.module';
import { FiTabsModule } from './tabs/tabs.module';
import { FiInactivityCountdownNotificationModule } from './inactivity-countdown-notification/inactivity-countdown-notification.module';
import { FiUpgradeCountdownNotificationModule } from './upgrade-countdown-notification/upgrade-countdown-notification.module';
import { FiNotificationModule } from './notification/notification.module';
import { PageTitleService } from './fi-services/page-title.service';
import { FiTextItemLinksToPopupDirective } from './text-item-links-to-popup/text-item-links-to-popup.directive';
import { AutocompleteModule } from './autocomplete/autocomplete.module';
import { ToggleDropdownModule } from '@fi-shared/fi-directives/toggle-dropdown'; // todo: import in shared module and the rest module that are imported from shared folder
import { EditTabsModule } from './edit-tabs/edit-tabs.module';
import { ButtonBackModule } from './button-back/button-back.module';
import { CropImageModule } from './crop-image/crop-image.module';
import { FiSelectPeriodModule } from '@fi-shared/fi-select-period';
import { FiDatePickerModule } from '@fi-shared/fi-date-picker';
import { FiCheckboxModule } from '@fi-shared/checkbox';
import { FiCheckboxListModule } from '@fi-shared/checkbox-list';
import { FiObjectsSelectorComponentModule } from '@fi-shared/objects-selector';
import { FiAccessRightFiltersModule } from '@fi-shared/access-right-filters';
import { InfoIconModule } from '@fi-app/shared/info-icon/info-icon.module';
import { AppTextService } from '@fi-shared/app-text';
import { FiNotificationItemsModule } from '@fi-app/utilities/notification/notification-items.module';
import { FiSharedActionsService } from '@fi-app/utilities/fi-services/fi-shared-actions.service';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { SelectSearchModule } from '@fi-shared/fi-select-search/select-search.module';
import { FiCountriesSelectModule } from '@fi-shared/fi-contacts/fi-countries-select/fi-countries-select.module';
import { FiTextEditorModule } from '@fi-shared/text-editor/text-editor.module';
import { FiContenteditableModelModule } from '@fi-shared/contenteditable-model/contenteditable-model.module';
import { OperationOverlaySmallModule } from '@fi-shared/operation-overlay-small/operation-overlay-small.module';
import { FiDirectivesModule } from '@fi-shared/fi-directives/fi-directives.module';
import { EmailAddressContactComponent } from './email-address-contacts/email-address-contact/email-address-contact.component';
import { FiMenuItemSearchModule } from '@fi-shared/fi-menu-item-search/fi-menu-item-search.module';
import { FiContactsModule } from '@fi-shared/fi-contacts/fi-contacts.module';
import { FiCountriesMenuModule } from '@fi-app/utilities/fi-countries-menu/fi-countries-menu.module';
import { SideListModule } from '@fi-app/utilities/side-list/side-list.module';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        BrowserModule,
        BrowserAnimationsModule,
        MatRadioModule,
        MatCheckboxModule,
        MatTabsModule,
        MatSliderModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        UtilitiesCommonModule,
        SharedModule,
        CompareHtmlModule,
        FiDisableLinksModule,
        FiToggleVisibilityModule,
        FiTelHrefModule,
        FiErrorMessageModule,
        FiTextEditorModule,
        FiContenteditableModelModule,
        FiReplaceWithLinksModule,
        FiAutofocusModule,
        FiClickTriggerForModule,
        FiDialogModule,
        FiVersionsListModule,
        FiRollupContentModule,
        OperationOverlaySmallModule,
        FiTagModule,
        InsertMediaModule,
        FileUploadModule,
        FiMobileAppHelpersModule,
        FiErrorMessageModule,
        FiSideListNewModule,
        FiTabsModule,
        FiInactivityCountdownNotificationModule,
        FiUpgradeCountdownNotificationModule,
        FiNotificationModule,
        FiNotificationItemsModule,
        AutocompleteModule,
        ToggleDropdownModule,
        EditTabsModule,
        SideListModule,
        ButtonBackModule,
        CropImageModule,
        FiSelectPeriodModule,
        FiDatePickerModule,
        FiCheckboxModule,
        FiCheckboxListModule,
        FiObjectsSelectorComponentModule,
        FiAccessRightFiltersModule,
        FiDirectivesModule,
        InfoIconModule,
        MatSelectModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        SelectSearchModule,
        MatRippleModule,
        FiCountriesSelectModule,
        FiCountriesMenuModule,
        FiMenuItemSearchModule,
        FiContactsModule,
    ],
    declarations: [
        AddressComponent,
        AddressesComponent,
        PageSectionErrorComponent,
        SectionSelectComponent,
        MobileTabsComponent,
        DeviceSwitchComponent,
        DeviceSwitchHandlerDirective,
        ItemActivationComponent,
        EmailAddressContactsComponent,
        MobileLanguagesSelectComponent,
        MoreMenuMobileComponent,
        TitleEditPipe,
        TitleCreatePipe,
        YesNoPipe,
        UserProfileLinkComponent,
        MoreMenuMobileDialogHandlerComponent,
        SecondsToTimePipe,
        FiTextItemLinksToPopupDirective,
        EmailAddressContactComponent,
    ],
    exports: [
        SharedModule,
        UtilitiesCommonModule,
        ButtonBackModule,
        InfoIconModule,
        MobileTabsComponent,
        DeviceSwitchComponent,
        DeviceSwitchHandlerDirective,
        TabsComponent,
        EditTabsModule,
        SideListModule,
        FiDirectivesModule,
        ToggleDropdownModule,
        AddressesComponent,
        ItemActivationComponent,
        EmailAddressContactsComponent,
        TitleEditPipe,
        TitleCreatePipe,
        YesNoPipe,
        MobileLanguagesSelectComponent,
        MoreMenuMobileComponent,
        UserProfileLinkComponent,
        CompareHtmlModule,
        FiDisableLinksModule,
        SecondsToTimePipe,
        FiToggleVisibilityModule,
        FiTelHrefModule,
        FiTextEditorModule,
        FiContenteditableModelModule,
        FiReplaceWithLinksModule,
        FiAutofocusModule,
        FiDialogModule,
        FiVersionsListModule,
        FiRollupContentModule,
        FiTagModule,
        InsertMediaModule,
        FileUploadModule,
        FiMobileAppHelpersModule,
        FiSideListNewModule,
        FiTabsModule,
        FiInactivityCountdownNotificationModule,
        FiUpgradeCountdownNotificationModule,
        FiNotificationModule,
        FiNotificationItemsModule,
        AddressComponent,
        FiTextItemLinksToPopupDirective,
        AutocompleteModule,
        CropImageModule,
        SectionSelectComponent,
        FiSelectPeriodModule,
        FiDatePickerModule,
        FiCheckboxModule,
        FiCheckboxListModule,
        FiObjectsSelectorComponentModule,
        FiAccessRightFiltersModule,
        FiCountriesSelectModule,
    ],
    providers: [
        AppTextService,
        FiBackendService,
        ObjectTypesService,
        ErrorMessageService,
        ObjectTitleService,
        YesNoPipe,
        ContactService,
        AuthenticateService,
        SessionService,
        ContentLanguagesService,
        ListViewSettingsService,
        ExtendedSearchService,
        LocalSearchStorageService,
        AccessRightService,
        FormValidatorService,
        SecondsToTimeService,
        InactivityCountdownHttpHandlerService,
        PageTitleService,
        {
            provide: SharedActionsBaseService,
            useExisting: FiSharedActionsService,
        },
        FiSharedActionsService,
    ],
})
export class UtilitiesModule {}
